import Footer from '@react/shared/components/Footer';
import Image from '@react/shared/components/Image';
import { RESOURCE_CENTER_PATH } from '@sly/core/constants/dashboardAppPaths';
import { parse } from 'query-string';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import AuthorArticles from 'sly/resourceCenter/components/AuthorArticles';
import Header from 'sly/resourceCenter/components/Header';
import Helmet from 'sly/resourceCenter/components/Helmet';
import SubscribeEmail from 'sly/resourceCenter/components/SubscribeEmail';
import { usePrefetch } from 'sly/services/api/prefetch';
import { Link } from 'sly/system';

import { pagePadding } from '../../styles';
const RESOURCE_CENTER_AUTHOR_INDEX_PATH = `${RESOURCE_CENTER_PATH}/authors`;
import BreadCrumb from '@react/shared/components/BreadCrumb';
import { CMSRequestInfo } from '@react/shared/types/requestInfo';
import { Article, Author, AuthorBody } from '@react/shared/types/usePrefetch';
import cx from 'classnames';
import { useRouter } from 'next/router';
import Arrow from 'sly/icons/Arrow';
import { assetPath } from 'sly/lib/path';
import ResourceCenterChatBotWrapper from 'sly/resourceCenter/components/ResourceCenterChatBotWrapper';
import { ARTICLES_RANGE_FOR_PAGINATION } from 'sly/resourceCenter/helpers';
import { pageNames, useEvents } from 'sly/services/events';
import Heading from 'sly/system/Heading';

interface AuthorPageParams {
  params: {
    slug: string;
  };
}

function arrayFilter<ArrayType>(
  arr: ArrayType[] | undefined,
  key: keyof ArrayType
) {
  if (!arr) return [];
  return arr.filter((item) => item[key]).map((item) => item);
}

const AuthorArticlesWrapper = ({
  author,
  pageNumber,
}: {
  author: AuthorBody;
  pageNumber: string;
}) => {
  const { firstName, slug } = author;
  const router = useRouter();
  const { requestInfo }: { requestInfo: CMSRequestInfo<Article> } = usePrefetch(
    'getArticle',
    {
      'filters[author][slug][$eq]': encodeURIComponent(slug),
      'pagination[pageSize]': ARTICLES_RANGE_FOR_PAGINATION,
      'pagination[page]': router.query?.['page-number'] || 1,
      populate: '*',
      sort: 'id:desc',
    }
  );

  return (
    <div
      className='mx-auto md:w-[42.5rem] lg:w-[64.5rem]'
      data-tp-id='gen-div-54e789e8-5144-4d6f-96c2-e590c610e3ab'
    >
      <AuthorArticles
        articlesCount={requestInfo.body.meta.pagination.total}
        slug={slug}
        firstName={firstName}
        pageNumber={pageNumber}
        articlesList={requestInfo.body.data}
      />
    </div>
  );
};

const AuthorPage = ({ params }: AuthorPageParams) => {
  const { events } = useEvents();
  useEffect(() => {
    events.page(pageNames.AuthorProfile, {});
  }, []);

  const location = useLocation();
  const { slug } = params;
  const { 'page-number': pageNumber } = parse(location.search);

  // Note : Slug should always be url encoded, encode it while redirecting
  const { requestInfo }: { requestInfo: CMSRequestInfo<Author> } = usePrefetch(
    'getAuthor',
    {
      'filters[slug][$eq]': encodeURIComponent(slug),
      populate: '*',
    }
  );

  if (!requestInfo.hasFinished) {
    return (
      <div
        className='flex h-[100vh]  flex-col items-center justify-center'
        data-tp-id='gen-div-db0ba05a-f3e8-4b0e-a99b-0569470d024a'
      >
        <Image
          width='250'
          height='250'
          src={assetPath('uploads/react-assets/homebase/loader.svg')}
          alt='loader'
          data-tp-id='gen-Image-589929e5-d193-47d3-9b1d-0793ce204a13'
        />
      </div>
    );
  }

  if (requestInfo.result?.data?.length === 0) {
    return null;
  }

  const author = requestInfo?.result?.data[0];
  const {
    slug: _slug,
    fullName,
    shortDescription,
    fullDescription,
    img,
  } = author || {};

  const paths = [
    {
      label: 'Resource Center Home',
      path: RESOURCE_CENTER_PATH,
    },
    {
      label: 'Authors',
      path: RESOURCE_CENTER_PATH + '/authors',
    },
    {
      label: fullName,
      path: '',
    },
  ];

  const alumniOf = arrayFilter(author?.alumniOf, 'showInUI');
  const award = arrayFilter(author?.award, 'showInUI');
  const knowsAbout = arrayFilter(author?.knowsAbout, 'showInUI');

  return (
    <>
      <ResourceCenterChatBotWrapper>
        <Helmet
          path={`${RESOURCE_CENTER_PATH}/author/${_slug}`}
          title={fullName}
          shortDescription={shortDescription}
          isAuthorPage
          author={author}
        />
        <Header />
        <div className={`${pagePadding} mx-auto mt-6 md:w-[42.5rem]`}>
          <BreadCrumb items={paths} />
        </div>
        <div
          className={cx(
            pagePadding,
            'flex',
            'items-center',
            'flex-col',
            'px-0 pt-6 pb-20'
          )}
          data-tp-id='gen-div-122037d2-c403-4302-858e-24a2b25ac994'
        >
          <div
            className='aspect-w-1 h-[15rem] w-[15rem] overflow-hidden rounded-[50%]'
            data-tp-id='gen-div-b388a741-7ca0-4c9e-a0ef-2c59f6c68af8'
          >
            <Image
              path={img?.path}
              alt={img?.alternativeText}
              sizes='(max-width: 727px) 160px, 240px'
              layout='fill'
              data-tp-id='gen-Image-b330803d-3cec-4cbd-a72d-fd39e2a81cbe'
              objectFit='cover'
            />
          </div>
          <Heading
            as='h1'
            className='mb-6 pt-l'
            data-tp-id='gen-Heading-15e57d28-1d5c-42ec-b2d7-4d99edf1d964'
            font='font-t-xl'
          >
            {fullName}
          </Heading>
          <div
            dangerouslySetInnerHTML={{ __html: fullDescription }}
            className='dynamic-editor mx-auto md:w-[42.5rem]'
          />
          {alumniOf && alumniOf.length > 0 && (
            <div className='dynamic-editor w-full md:mx-auto md:w-[42.5rem]'>
              <strong>Alumni Of:</strong>
              <ul>
                {alumniOf.map((item) => (
                  <li key={item.id}>{item.alumniOf}</li>
                ))}
              </ul>
            </div>
          )}
          {award && award?.length > 0 && (
            <div className='dynamic-editor w-full md:mx-auto md:w-[42.5rem]'>
              <strong>Awards:</strong>
              <ul>
                {award.map((item) => (
                  <li key={item.id}>{item.Award}</li>
                ))}
              </ul>
            </div>
          )}
          {knowsAbout && knowsAbout?.length > 0 && (
            <div className='dynamic-editor w-full md:mx-auto md:w-[42.5rem]'>
              <strong>Expertise in:</strong>
              <ul>
                {knowsAbout.map((item) => (
                  <li key={item.id}>{item.name}</li>
                ))}
              </ul>
            </div>
          )}
          <div
            className='left-0 right-auto mt-12 w-full max-w-[42.5rem]'
            data-tp-id='gen-div-404609ce-658f-4517-8e32-62434da3a887'
          >
            <Link
              className='font-b-l'
              to={RESOURCE_CENTER_AUTHOR_INDEX_PATH}
              data-tp-id='gen-Link-f03e2114-80a7-4e05-9cf4-4b7efababb10'
            >
              <div
                className='flex underline'
                data-tp-id='gen-div-95a4d2cd-ccf8-4500-926d-d24f1de37f49'
              >
                <div data-tp-id='gen-div-c80f10d4-2208-44ec-82a0-e2956081a57e'>
                  See other Seniorly authors{' '}
                </div>
                <div
                  className='flex flex-col justify-center'
                  data-tp-id='gen-div-8f906a4e-cf26-4ee4-88e6-ff1382bd110c'
                >
                  <Arrow className='rotate-180' />
                </div>
              </div>
            </Link>
          </div>
        </div>
        <hr />
        <div
          className={cx(pagePadding, 'mt-xxl md:mt-xxxl')}
          data-tp-id='gen-div-0130c3b4-5971-4610-8840-8f98dc75c060'
        >
          <AuthorArticlesWrapper
            author={author}
            pageNumber={pageNumber as string}
          />
        </div>
        <SubscribeEmail />
        <Footer />
      </ResourceCenterChatBotWrapper>
    </>
  );
};

AuthorPage.displayName = 'Author';

export default AuthorPage;
